<template>
  <layout width="428">
    <template slot="header">
      Edit property
    </template>
    <template>
      <div class="mx-4 mb-2 mt-4">
        <div class="subtitle-1 mb-0">
          <span class="font-weight-bold">Property:</span>
          {{ currentItem.property }}
        </div>
        <div class="subtitle-1 mb-0">
          <span class="font-weight-bold">Name: </span>{{ currentItem.title }}
        </div>
        <div class="subtitle-1 mb-0">
          <span class="font-weight-bold">Key: </span>{{ currentItem.key }}
        </div>
        <div class="subtitle-1 ">
          <span class="font-weight-bold">Type:</span> {{ currentItem.type }}
        </div>
        <div class="subtitle-1 d-flex justify-space-between">
          <div>
            <span class="font-weight-bold">Value:</span>
            <span>
              {{ getValue }}
            </span>
          </div>
          <div>
            <v-btn @click.stop="handleCopy" icon>
              <v-icon>
                mdi-content-copy
              </v-icon>
            </v-btn>
          </div>
        </div>
      </div>
      <template v-if="!currentItem.valueSet && !currentItem.valueRange">
        <v-select
          :menu-props="{ bottom: true, offsetY: true }"
          v-if="isBoolean(currentItem.typeId)"
          label="Value"
          v-model="innerValue"
          :data-test-select="currentItem.property"
          item-value="id"
          item-text="name"
          :items="[
            { id: true, name: 'true' },
            { id: false, name: 'false' },
            { id: null, name: 'null' }
          ]"
          class="px-4 pb-4"
        >
          <template v-slot:item="{ item }">
            <div :data-test-select-value="item.name">
              {{ item.name }}
            </div>
          </template>
        </v-select>
        <code-editor
          v-model="innerValue"
          @is-error="isCodeError = $event"
          v-else-if="isJson(currentItem.typeId)"
          :code="currentItem.value"
        ></code-editor>

        <template v-else>
          <v-text-field
            v-if="isNumber(currentItem.typeId)"
            type="number"
            :data-test="currentItem.property"
            class="px-4"
            label="Value"
            clearable
            :placeholder="currentItem.property"
            v-model.number="innerValue"
          ></v-text-field>
          <v-text-field
            :data-test="currentItem.property"
            v-else
            class="px-4"
            label="Value"
            clearable
            :placeholder="currentItem.property"
            v-model="innerValue"
          ></v-text-field>
        </template>
      </template>
      <template v-else>
        <v-select
          :menu-props="{ bottom: true, offsetY: true }"
          v-if="
            currentItem.valueSet && currentItem.valueSet.component === 'select'
          "
          :data-test-select="currentItem.property"
          v-model="innerValue"
          item-value="key"
          class="px-4 pb-4"
          item-text="title"
          :items="currentItem.valueSet.list"
        >
          <template v-slot:item="{ item }">
            <div :data-test-select-value="item.title">
              {{ item.title }}
            </div>
          </template>
        </v-select>
        <v-text-field
          :data-test="currentItem.property"
          v-else-if="currentItem.valueSet"
          v-model="innerValue"
          class="px-4 pb-4"
        ></v-text-field>
        <v-text-field
          :data-test="currentItem.property"
          v-if="currentItem.valueRange"
          v-model.number="innerValue"
          class="px-4 pb-4"
          :max="currentItem.valueRange.max"
          :min="currentItem.valueRange.min"
          type="number"
        ></v-text-field>
      </template>
    </template>
    <template slot="footer">
      <v-spacer />
      <v-btn text @click="close">Cancel</v-btn>
      <v-btn
        data-test="saveProperty"
        color="primary"
        text
        @click="saveHandler"
        :disabled="isCodeError || loading || localLoading"
        :loading="loading || localLoading"
        >save</v-btn
      >
    </template>
  </layout>
</template>

<script>
import Layout from '@/layouts/LayoutPopup';
import {
  computed,
  defineComponent,
  ref,
  onMounted
} from '@vue/composition-api';
import { useMutation } from '@vue/apollo-composable';
import gql from '@/api/objects/item.graphql';
import CodeEditor from '@/components/CodeEditor';
import { isJson, isBoolean, isNumber } from '@/utils/getCurrentType';
import { truncateString } from '@/utils/truncate';
import copy from 'copy-to-clipboard';

export default defineComponent({
  name: 'EditProperty',
  components: {
    CodeEditor,
    Layout
  },
  props: {
    currentItem: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const { mutate: updateProperty } = useMutation(gql.updateObjectProperty);

    const localLoading = ref(false);
    const innerValue = ref();
    const isCodeError = ref(false);

    const close = () => {
      emit('close');
    };

    const prepareBeforeSend = value => {
      if (isJson(props.currentItem.typeId)) {
        if (typeof value === 'string') {
          return JSON.parse(value);
        } else {
          return value;
        }
      } else if (isNumber(props.currentItem.typeId)) {
        return +value;
      }
      return value;
    };

    const getValue = computed(() => {
      return truncateString(`${props.currentItem.value}`, 40);
    });

    const saveHandler = () => {
      localLoading.value = true;
      updateProperty({
        input: {
          id: props.currentItem.id,
          patch: {
            value: prepareBeforeSend(innerValue.value)
          }
        }
      })
        .then(() => {
          close();
        })
        .finally(() => {
          localLoading.value = false;
        });
    };

    const handleCopy = () => {
      copy(props.currentItem.value, {
        format: 'text/plain'
      });
    };

    const copyAll = () => {
      copy(
        JSON.stringify({
          property: props.currentItem.property,
          name: props.currentItem.title,
          key: props.currentItem.key,
          type: props.currentItem.type,
          value: getValue.value
        }),
        {
          format: 'text/plain'
        }
      );
    };

    onMounted(() => {
      if (isJson(props.currentItem.typeId)) {
        innerValue.value = JSON.parse(props.currentItem.value);
      } else {
        innerValue.value = props.currentItem.value;
      }
    });

    return {
      isCodeError,
      innerValue,
      saveHandler,
      close,
      isJson,
      isBoolean,
      isNumber,
      getValue,
      localLoading,
      handleCopy,
      copyAll
    };
  }
});
</script>
